import { ethers } from 'ethers';
import TokenABI from './Token.json';
import { trustlineService } from '../services/trustline';

export interface ERC3643Info {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: string;
  balance: string;
  isPaused: boolean;
}


// ERC3643 Contract Functions
export const getERC3643Contract = (provider: ethers.Provider, contractAddress: string) => {
  return new ethers.Contract(contractAddress, TokenABI, provider);
};

export const getERC3643SignerContract = (signer: ethers.Signer, contractAddress: string) => {
  return new ethers.Contract(contractAddress, TokenABI, signer);
};

export const validateERC3643Contract = async (provider: ethers.Provider, contractAddress: string): Promise<boolean> => {
  try {
    // Validate that the contract has a Trustline compliance module
    await getTrustlineComplianceModuleAddress(provider, contractAddress);
    return true;
  } catch (error) {
    console.warn('Token Contract validation failed:', error);
    return false;
  }
};

export const formatAmount = (amount: string, decimals: number = 18): string => {
  return ethers.formatUnits(amount, decimals);
};

export const parseAmount = (amount: string, decimals: number = 18): string => {
  return ethers.parseUnits(amount, decimals).toString();
};


// ERC3643 Helper Functions
export const getERC3643Info = async (provider: ethers.Provider, contractAddress: string, userAddress: string): Promise<ERC3643Info> => {
  const contract = getERC3643Contract(provider, contractAddress);
  
  const [name, symbol, decimals, totalSupply, balance, isPaused] = await Promise.all([
    contract.name(),
    contract.symbol(),
    contract.decimals(),
    contract.totalSupply(),
    contract.balanceOf(userAddress),
    contract.paused()
  ]);
  
  return {
    name,
    symbol,
    decimals: Number(decimals),
    totalSupply: totalSupply.toString(),
    balance: balance.toString(),
    isPaused
  };
};

export const checkIsAgent = async (provider: ethers.Provider, contractAddress: string, userAddress: string): Promise<boolean> => {
  try {
    const contract = getERC3643Contract(provider, contractAddress);
    return await contract.isAgent(userAddress);
  } catch (error) {
    console.warn('Failed to check agent status:', error);
    return false;
  }
};

export const getAllowance = async (
  provider: ethers.Provider,
  contractAddress: string,
  owner: string,
  spender: string
): Promise<string> => {
  try {
    const contract = getERC3643Contract(provider, contractAddress);
    // allowance(owner, spender) - returns the amount of tokens that spender is allowed to spend on behalf of owner
    const allowanceAmount = await contract.allowance(owner, spender);
    return allowanceAmount.toString();
  } catch (error) {
    console.warn('Failed to fetch allowance:', error);
    throw new Error('Could not fetch allowance');
  }
};

export const performTransfer = async (
  signer: ethers.Signer, 
  contractAddress: string, 
  to: string, 
  amount: string,
  chainId?: number,
  fromAddress?: string
): Promise<ethers.TransactionResponse> => {
  // TrustLine validation for transfer
  if (chainId && fromAddress) {
    const provider = signer.provider;
    if (!provider) {
      throw new Error('Provider not available for TrustLine validation');
    }
    
    const isApproved = await trustlineService.validateTransfer(
      chainId,
      fromAddress,
      contractAddress,
      fromAddress, // from
      to, // to
      amount,
      provider
    );

    if (!isApproved) {
      throw new Error('Transfer transaction not approved by TrustLine trusted providers');
    }
  }

  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.transfer(to, amount);
};

export const performTransferFrom = async (
  signer: ethers.Signer, 
  contractAddress: string, 
  from: string,
  to: string, 
  amount: string,
  chainId?: number,
  senderAddress?: string
): Promise<ethers.TransactionResponse> => {
  // TrustLine validation for transferFrom
  if (chainId && senderAddress) {
    const provider = signer.provider;
    if (!provider) {
      throw new Error('Provider not available for TrustLine validation');
    }
    
    const isApproved = await trustlineService.validateTransferFrom(
      chainId,
      senderAddress,
      contractAddress,
      from, // from
      to, // to
      amount,
      provider
    );

    if (!isApproved) {
      throw new Error('TransferFrom transaction not approved by TrustLine trusted providers');
    }
  }

  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.transferFrom(from, to, amount);
};

export const performMint = async (
  signer: ethers.Signer, 
  contractAddress: string, 
  to: string, 
  amount: string,
  chainId?: number,
  senderAddress?: string
): Promise<ethers.TransactionResponse> => {
  // TrustLine validation for mint
  if (chainId && senderAddress) {
    const provider = signer.provider;
    if (!provider) {
      throw new Error('Provider not available for TrustLine validation');
    }
    
    const isApproved = await trustlineService.validateMint(
      chainId,
      senderAddress,
      contractAddress,
      to, // to
      amount,
      provider
    );

    if (!isApproved) {
      throw new Error('Mint transaction not approved by TrustLine trusted providers');
    }
  }

  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.mint(to, amount);
};

export const performBurn = async (
  signer: ethers.Signer, 
  contractAddress: string, 
  userAddress: string, 
  amount: string,
  chainId?: number,
  senderAddress?: string
): Promise<ethers.TransactionResponse> => {
  // TrustLine validation for burn
  if (chainId && senderAddress) {
    const provider = signer.provider;
    if (!provider) {
      throw new Error('Provider not available for TrustLine validation');
    }
    
    const isApproved = await trustlineService.validateBurn(
      chainId,
      senderAddress,
      contractAddress,
      userAddress, // from
      amount,
      provider
    );

    if (!isApproved) {
      throw new Error('Burn transaction not approved by TrustLine trusted providers');
    }
  }

  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.burn(userAddress, amount);
};

export const setAddressFrozen = async (signer: ethers.Signer, contractAddress: string, userAddress: string, freeze: boolean): Promise<ethers.TransactionResponse> => {
  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.setAddressFrozen(userAddress, freeze);
};

// Helper function to fetch Trustline compliance module address
export const getTrustlineComplianceModuleAddress = async (
  provider: ethers.Provider,
  tokenAddress: string
): Promise<string> => {
  try {
    // First, get the compliance contract address from the token
    // Note: The compliance() function might not be in the standard ERC3643 ABI
    // We'll create a contract with just the compliance function
    const tokenContract = new ethers.Contract(
      tokenAddress,
      ['function compliance() external view returns (address)'],
      provider
    );
    
    const complianceAddress = await tokenContract.compliance();
    
    // Then, get the modules array from the compliance contract
    const complianceContract = new ethers.Contract(
      complianceAddress,
      ['function getModules() external view returns (address[] memory)'],
      provider
    );
    
    const modules = await complianceContract.getModules();
    
    if (modules.length === 0) {
      throw new Error('No modules found in compliance contract');
    }
    
    // Iterate through all modules and find the one with name "TrustlineModule"
    for (const moduleAddress of modules) {
      try {
        // Create a contract instance for the module to read its name
        const moduleContract = new ethers.Contract(
          moduleAddress,
          ['function name() external view returns (string memory)'],
          provider
        );
        
        const moduleName = await moduleContract.name();
        
        if (moduleName === 'TrustlineModule') {
          return moduleAddress;
        }
      } catch (error) {
        // If we can't read the name from this module, continue to the next one
        console.warn(`Failed to read name from module at ${moduleAddress}:`, error);
        continue;
      }
    }
    
    // If no module with matching name is found
    throw new Error('No TrustlineModule found in compliance contract');
  } catch (error) {
    console.error('Failed to fetch Trustline compliance module address:', error);
    throw new Error('Could not fetch Trustline compliance module address');
  }
};

export const freezePartialTokens = async (signer: ethers.Signer, contractAddress: string, userAddress: string, amount: string): Promise<ethers.TransactionResponse> => {
  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.freezePartialTokens(userAddress, amount);
};

export const unfreezePartialTokens = async (signer: ethers.Signer, contractAddress: string, userAddress: string, amount: string): Promise<ethers.TransactionResponse> => {
  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.unfreezePartialTokens(userAddress, amount);
};

export const pauseToken = async (signer: ethers.Signer, contractAddress: string): Promise<ethers.TransactionResponse> => {
  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.pause();
};

export const unpauseToken = async (signer: ethers.Signer, contractAddress: string): Promise<ethers.TransactionResponse> => {
  const contract = getERC3643SignerContract(signer, contractAddress);
  return await contract.unpause();
};
